/**
 * Shared mapping from a backend FilterConditionType to its display label.
 *
 * The same list is currently duplicated in `Condition.vue` and `ExecutionDetails.vue`; both omit
 * `DEFAULT`, which the backend does emit for a router's fallback branch. New code should read from
 * here, and those two should eventually be migrated onto it rather than drifting further apart.
 *
 * The table itself stays private: everything callers need is answered by the four accessors below,
 * so a consumer never has to know the shape of a row. Export it if a dropdown ever needs to
 * enumerate the operators — that is the one case the accessors cannot serve.
 */

interface ConditionOperator {
  key: string;
  /** i18n key under the `Condition.*` namespace. */
  label: string;
  /** False for operators the engine answers without a left-hand value. */
  showLeft: boolean;
  /** False for operators the engine answers from the left-hand value alone. */
  showRight: boolean;
}

const CONDITION_OPERATORS: ConditionOperator[] = [
  {
    key: "EQUAL_TO",
    label: "Condition.EqualTo",
    showLeft: true,
    showRight: true,
  },
  {
    key: "NOT_EQUAL_TO",
    label: "Condition.NotEqualTo",
    showLeft: true,
    showRight: true,
  },
  {
    key: "CONTAINS",
    label: "Condition.Contains",
    showLeft: true,
    showRight: true,
  },
  {
    key: "DOES_NOT_CONTAIN",
    label: "Condition.DoesNotContain",
    showLeft: true,
    showRight: true,
  },
  {
    key: "STARTS_WITH",
    label: "Condition.StartsWith",
    showLeft: true,
    showRight: true,
  },
  {
    key: "DOES_NOT_START_WITH",
    label: "Condition.DoesNotStartWith",
    showLeft: true,
    showRight: true,
  },
  {
    key: "IS_GREATER_THAN",
    label: "Condition.IsGreaterThan",
    showLeft: true,
    showRight: true,
  },
  {
    key: "IS_LESS_THAN",
    label: "Condition.IsLessThan",
    showLeft: true,
    showRight: true,
  },
  { key: "IS_ODD", label: "Condition.IsOdd", showLeft: true, showRight: false },
  {
    key: "IS_EVEN",
    label: "Condition.IsEven",
    showLeft: true,
    showRight: false,
  },
  {
    key: "IS_NULL",
    label: "Condition.IsNull",
    showLeft: true,
    showRight: false,
  },
  {
    key: "IS_NOT_NULL",
    label: "Condition.IsNotNull",
    showLeft: true,
    showRight: false,
  },
  {
    key: "IS_EMPTY",
    label: "Condition.IsEmpty",
    showLeft: true,
    showRight: false,
  },
  {
    key: "IS_NOT_EMPTY",
    label: "Condition.IsNotEmpty",
    showLeft: true,
    showRight: false,
  },
  {
    key: "STEP_FAILED",
    label: "Condition.DidStepFail",
    showLeft: false,
    showRight: false,
  },
  {
    key: "STEP_SUCCEEDED",
    label: "Condition.DidStepSucceed",
    showLeft: false,
    showRight: false,
  },
  {
    key: "STEP_FAILED_WITH_CODE",
    label: "Condition.DidStepFailWithCode",
    showLeft: true,
    showRight: true,
  },
  // Absent from the two existing copies. A router's fallback branch carries this and nothing else.
  {
    key: "DEFAULT",
    label: "Condition.DefaultCondition",
    showLeft: false,
    showRight: false,
  },
];

const OPERATORS_BY_KEY: Record<string, ConditionOperator> =
  CONDITION_OPERATORS.reduce((map, operator) => {
    map[operator.key] = operator;
    return map;
  }, {} as Record<string, ConditionOperator>);

function findConditionOperator(key: unknown): ConditionOperator | undefined {
  return typeof key === "string" ? OPERATORS_BY_KEY[key] : undefined;
}

/**
 * i18n key for an operator, or the raw key when it is unrecognised — showing `SOME_NEW_OPERATOR`
 * is more useful than showing nothing if the backend enum grows.
 */
export function conditionOperatorLabelKey(key: unknown): string {
  return findConditionOperator(key)?.label ?? String(key ?? "");
}

export function conditionTakesLeftValue(key: unknown): boolean {
  return findConditionOperator(key)?.showLeft ?? true;
}

export function conditionTakesRightValue(key: unknown): boolean {
  return findConditionOperator(key)?.showRight ?? true;
}

export function isDefaultCondition(key: unknown): boolean {
  return key === "DEFAULT";
}
